<script>
  import { onDestroy, onMount, tick } from "svelte";
  import { apiRequest, ApiError } from "../utils/api";
  import { groupDigits } from "../utils/format.js";
  import RunTimeline from "./RunTimeline.svelte";
  import RunStopReason from "./RunStopReason.svelte";

  // The agent run is anchored to a single generation. The parent (GenerationManager)
  // passes the generation and whether it is completed (a completed generation cannot
  // host a useful run — the API 422s).
  export let generationId;
  export let generationStatus = null;
  // Fired whenever this panel attaches to a run or its status changes, so the
  // parent's run-history rail can refresh (a new run appears; a run reaches a
  // terminal state). Called with (runId, status).
  export let onStatusChange = () => {};
  // Fired (no args) when a row-MUTATING tool call (post_transaction /
  // delete_transaction) newly COMPLETES during the run, so the parent can
  // re-fetch the generation detail and the bottom transactions table fills in
  // live. Fires at most once per poll cycle even if several mutating calls land
  // together, and once per tool_call id (never re-fires on later polls for the
  // same call).
  export let onRowsChanged = () => {};
  // Fired with the SET of rule ids the agent is currently stuck on, derived from
  // this run's timeline (Slice 5). The parent (GenerationManager) uses it to
  // highlight the matching cards in the Rules panel. Called with an array of ids
  // (may be empty) whenever the derived set changes.
  export let onFlaggedRulesChange = () => {};

  // Tools that WRITE GenerationRows — only these trigger a live table refresh.
  // get_generation_info / get_transactions / verify_generation / complete_generation
  // never add or remove spending rows, so they must not fire onRowsChanged.
  const ROW_MUTATING_TOOLS = new Set(["post_transaction", "delete_transaction"]);
  // Completed mutating tool_call ids we've already signalled, so a re-sent
  // (upserted) tool_call on a later poll doesn't fire the callback again.
  let seenMutatingToolCallIds = new Set();

  const ACTIVE_STATUSES = new Set(["init", "queued", "running", "paused"]);
  const POLL_INTERVAL_MS = 1200;

  let runId = null;
  let runStatus = null;
  let messages = [];
  let afterId = null;
  let updatedSince = null;
  let pendingApproval = null;
  let reopened = false; // server flag: this paused run was re-opened by an un-confirm

  // ----- Part C: per-run LLM model picker + budget/steps progress -----
  // Available to any authenticated user (managers + admins) — GET /api/llm_models
  // is not admin-gated. Fetched once on mount so the "Start run" composer can
  // offer a choice; defaults to the first (LlmModel.default order). With 0 or 1
  // models the select is hidden — there is nothing meaningful to choose, but the
  // single id (if any) is still sent so the run binds to it explicitly.
  let llmModels = [];
  let selectedLlmModelId = null;
  // Progress readout, filled in from every /messages poll payload.
  let runCost = null;
  let runStepCount = null;
  let runBudgetLimit = null;
  let runStepsLimit = null;
  // The THIRD safety limit. Run 33 was stopped by it while the panel showed only
  // the two that had NOT fired ($0.07/$10, 30/200 steps) — 1 012 020 of 1 000 000
  // tokens appeared nowhere on screen. Now it stands next to them.
  let runTokensTotal = null;
  let runTokensLimit = null;
  let runModelName = null;
  // Why the run ended, straight off the poll payload (loop.error_message). nil
  // while healthy.
  let runErrorMessage = null;

  async function loadLlmModels() {
    try {
      llmModels = await apiRequest("/api/llm_models");
      if (selectedLlmModelId == null && llmModels.length > 0) {
        selectedLlmModelId = llmModels[0].id;
      }
    } catch (_error) {
      // Non-fatal: the server falls back to LlmModel.default (or ENV) when no
      // llm_model_id is sent, so a failed fetch just loses the picker.
      llmModels = [];
    }
  }

  let starting = false;
  let startError = null;
  let stopping = false;
  let deciding = false;

  // ----- Reasoning show/hide (the "lamp") -----
  // Client-side only: hides the noisy "thinking" blocks in the timeline without
  // a reload. Default ON, persisted per-browser (not per-run) so the operator's
  // preference follows them across generations. Guarded so private-mode /
  // disabled storage never breaks the panel.
  const SHOW_REASONING_KEY = "agentRun.showReasoning";

  function loadShowReasoning() {
    try {
      const raw = window.localStorage.getItem(SHOW_REASONING_KEY);
      return raw === null ? true : raw === "true";
    } catch (_error) {
      return true;
    }
  }

  let showReasoning = loadShowReasoning();

  function toggleShowReasoning() {
    showReasoning = !showReasoning;
    try {
      window.localStorage.setItem(SHOW_REASONING_KEY, String(showReasoning));
    } catch (_error) {
      // Storage unavailable (private mode / disabled) — the toggle still works
      // in-memory; we simply don't persist.
    }
  }

  // ----- AUTO-APPROVE (client-side HITL automation) -----
  // Purely a UI convenience: it clicks the SAME approve path a human would. YOLO
  // auto-approves every gate; the counter auto-approves the next N gates then stops.
  // A terminal gate whose fresh verify FAILS is never auto-approved (mirrors
  // ApprovalCard's approveDisabled) — the human must handle a failing completion.
  let autoApprove = false; // YOLO — auto-approve every gate
  let autoApproveRemaining = 0; // live N counter — auto-approve the next N gates
  const autoApprovedGateIds = new Set();

  // ----- STEERING -----
  // The composer text, whether a send is in flight, and any transient error.
  // steeringBusyIds tracks pending steering messages with an edit/cancel request
  // in flight so their controls disable until it resolves.
  let steeringText = "";
  let sendingSteering = false;
  let steeringError = null;
  let steeringBusyIds = new Set();
  let driftBanner = false;
  let pollTimer = null;
  let pollGeneration = 0; // cancels stale poll loops when the run changes
  let destroyed = false;  // set on unmount so a late hydration fetch can't re-attach

  // The scrollable timeline container. We auto-scroll to the freshest message as
  // it streams in, but only when the user is already near the bottom.
  let timelineEl = null;
  const NEAR_BOTTOM_PX = 80;

  // ----- Slice 5: derive the rule ids the agent is currently BLOCKED on -----
  // The highlight must reflect the LATEST run state, so we take the most recent
  // verify_generation and the most recent post_transaction from the timeline (in
  // id order, tool calls carry the outcome once their result fills in):
  //   * the latest verify's error_rules → the rules that verification is failing,
  //   * the latest post's rejected_rule_id → the rule that rejected the last row.
  // A subsequent PASSING verify (empty error_rules) or an ACCEPTED post clears its
  // side, so a resolved rule stops being highlighted.
  function orderedToolCalls(msgs) {
    const calls = [];
    for (const message of msgs) {
      if (!Array.isArray(message.tool_calls)) continue;
      for (const toolCall of message.tool_calls) {
        calls.push(toolCall);
      }
    }
    // Messages already arrive id-sorted; tool_calls within a message are ordered.
    return calls;
  }

  function deriveFlaggedRuleIds(msgs) {
    const calls = orderedToolCalls(msgs);
    const flagged = new Set();

    // Latest verify_generation that has a result (is_success !== null).
    const verifyCall = [...calls]
      .reverse()
      .find((c) => c.function_name === "verify_generation" && c.is_success != null);
    if (verifyCall && Array.isArray(verifyCall.error_rules)) {
      for (const entry of verifyCall.error_rules) {
        if (entry && entry.rule_id != null) flagged.add(entry.rule_id);
      }
    }

    // Latest post_transaction that has a result: if it was a rule-attributed
    // rejection, flag that rule; if it was accepted, it clears nothing extra
    // (verify remains the authority) but a rejected rule is surfaced immediately.
    const postCall = [...calls]
      .reverse()
      .find((c) => c.function_name === "post_transaction" && c.is_success != null);
    if (postCall && postCall.accepted === false && postCall.rejected_rule_id != null) {
      flagged.add(postCall.rejected_rule_id);
    }

    return Array.from(flagged);
  }

  // Emit whenever the derived set changes (compared as a sorted signature so an
  // equal set doesn't spam the parent every poll).
  let lastFlaggedSignature = null;
  $: {
    const ids = deriveFlaggedRuleIds(messages);
    const signature = ids.slice().sort((a, b) => a - b).join(",");
    if (signature !== lastFlaggedSignature) {
      lastFlaggedSignature = signature;
      onFlaggedRulesChange(ids);
    }
  }

  $: isActive = ACTIVE_STATUSES.has(runStatus);
  $: awaitingApproval = runStatus === "paused" && !!pendingApproval;
  $: completedGeneration = generationStatus === "completed";
  // "Start run" is available whenever there is no ACTIVE run and the generation
  // can still host work — a fresh generation OR after a previous run ended ("Run
  // agent again"). Starting SENDS the composer text as the first user message.
  $: canStart = !isActive && !completedGeneration;
  // A REOPENED (un-confirmed) run is `paused` with the server `reopened` flag set.
  // We DON'T derive this from `paused && !pendingApproval` — that's transiently
  // true right after a HITL gate is approved (decide() clears pendingApproval
  // before the poll flips status off paused), which would misfire. The positive
  // server flag is unambiguous. In this state the composer's submit CONTINUES the
  // same loop (keeping its full history) instead of steering.
  $: reopenedPaused = runStatus === "paused" && reopened && !pendingApproval;

  // Seed the composer once when a reopened run appears so it's obvious what to do
  // (the user edits it). Reset the guard when we leave the reopened state.
  let reopenedPrefilled = false;
  $: if (reopenedPaused && !reopenedPrefilled) {
    steeringText = "Redo the last few spending rows differently: ";
    reopenedPrefilled = true;
  }
  $: if (!reopenedPaused) {
    reopenedPrefilled = false;
  }

  // Notify the parent (run-history rail) on every (runId, status) transition so
  // the archive list can refresh — a new run appears, and terminal runs get
  // their final badge/error. Firing on the tuple covers both a fresh attach and
  // an in-place status change; the parent dedupes/refetches cheaply.
  let lastNotified = null;
  $: {
    const key = `${runId}:${runStatus}`;
    if (runId && runStatus && key !== lastNotified) {
      lastNotified = key;
      onStatusChange(runId, runStatus);
    }
  }

  onDestroy(() => {
    destroyed = true;
    stopPolling();
  });

  // On a full page reload, re-attach to whatever run is already live for this
  // generation (running/paused, incl. a pending HITL gate) so the timeline and
  // approval card survive the reload without the user clicking Start again. The
  // reactive boundGenerationId block below runs BEFORE onMount, so it has already
  // set boundGenerationId to the current generation — this hydration won't be
  // clobbered by a spurious reset (the reset only fires on a real change).
  async function hydrateCurrent() {
    if (!generationId || completedGeneration || runId) {
      return;
    }
    const hydratingGeneration = generationId;
    try {
      const response = await apiRequest(`/api/generations/${generationId}/agent_runs/current`);
      // Re-check liveness AFTER the await: the panel may have been destroyed
      // (generation switch — onDestroy already ran, so nothing would ever cancel
      // a poll we start now), the user may have switched generations, or they may
      // have clicked Start (runId now set). Attaching in any of those cases leaks
      // an orphaned poll loop or clobbers the fresh run — so bail.
      if (destroyed || runId || boundGenerationId !== hydratingGeneration) {
        return;
      }
      if (response?.id) {
        attachRun(response.id, response.status);
      }
    } catch (_error) {
      // A hydration failure is non-fatal: the user can still click Start. Silently
      // fall through to the idle panel.
    }
  }
  onMount(hydrateCurrent);
  onMount(loadLlmModels);

  // When a completed generation is re-opened (un-confirmed) → draft, its loop was
  // reopened active (paused). Drop whatever terminal run we were showing and
  // attach fresh to the reopened run so its full history + the Continue composer
  // appear without a page reload. Fires only on the completed→not-completed edge.
  let prevCompletedGen = completedGeneration;
  $: {
    const nowCompleted = completedGeneration;
    if (prevCompletedGen && !nowCompleted) {
      resetRunState();
      hydrateCurrent();
    }
    prevCompletedGen = nowCompleted;
  }

  function stopPolling() {
    pollGeneration += 1;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  }

  function resetRunState() {
    stopPolling();
    runId = null;
    runStatus = null;
    messages = [];
    afterId = null;
    updatedSince = null;
    pendingApproval = null;
    reopened = false;
    driftBanner = false;
    startError = null;
    runCost = null;
    runStepCount = null;
    runBudgetLimit = null;
    runStepsLimit = null;
    runTokensTotal = null;
    runTokensLimit = null;
    runModelName = null;
    runErrorMessage = null;
    seenMutatingToolCallIds = new Set();
    steeringText = "";
    steeringError = null;
    sendingSteering = false;
    steeringBusyIds = new Set();
  }

  // When the selected generation changes, drop the previous run's state.
  let boundGenerationId = null;
  $: if (generationId !== boundGenerationId) {
    boundGenerationId = generationId;
    resetRunState();
    // Pre-fill the composer with the kickoff so the panel opens ready to Send/Start
    // (unless the generation is completed — no run can do useful work there). If a
    // live run is re-attached in onMount, attachRun clears this for steering.
    if (generationId && generationStatus !== "completed") {
      steeringText = defaultKickoff(generationId);
    }
  }

  // The kickoff the composer is PRE-FILLED with before a run starts — mirrors the
  // server's fallback template (agent_runs_controller#kickoff_message). "Start run"
  // sends this (possibly edited) text as the first user message, exactly like Send.
  function defaultKickoff(id) {
    return (
      `Generate the spending rows for generation #${id}. ` +
      `Use get_generation_info with generation_id ${id} for its target balance, source transactions, catalog, and guardrails. ` +
      `Work to the target while respecting all rules, verify the generation, and only then complete it.`
    );
  }

  async function startRun() {
    if (!generationId || starting || isActive) {
      return;
    }
    starting = true;
    startError = null;
    // Start === Send: post the composer text as the first user message so it shows
    // as the user's own bubble. Blank composer falls back to the server template.
    const message = steeringText.trim();
    const body = message ? { message } : {};
    // Send the picked model explicitly whenever we have one (including the sole
    // entry when there's only one) — the server falls back to LlmModel.default
    // when omitted, so this is only needed to honour an actual choice.
    if (selectedLlmModelId != null) {
      body.llm_model_id = selectedLlmModelId;
    }
    try {
      const response = await apiRequest(`/api/generations/${generationId}/agent_runs`, {
        method: "POST",
        body
      });
      steeringText = ""; // consumed as the first message; attachRun also resets it
      attachRun(response.id, response.status);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.payload?.agent_run_id) {
        // A run already exists for this generation — surface it instead of erroring.
        attachRun(error.payload.agent_run_id, error.payload.status);
      } else if (error instanceof ApiError && error.status === 422) {
        startError = error.message || "This generation is already completed; a run can't do useful work.";
      } else {
        startError = error?.message || "Could not start the agent run.";
      }
    } finally {
      starting = false;
    }
  }

  function attachRun(id, status) {
    resetRunState();
    runId = id;
    runStatus = status;
    beginPolling();
  }

  function beginPolling() {
    stopPolling();
    const generation = ++pollGeneration;
    pollOnce(generation);
  }

  async function pollOnce(generation) {
    if (generation !== pollGeneration || !runId) {
      return;
    }
    try {
      const params = new URLSearchParams();
      if (afterId) params.set("after_id", afterId);
      if (updatedSince) params.set("updated_since", updatedSince);
      const query = params.toString() ? `?${params}` : "";
      const payload = await apiRequest(`/api/agent_runs/${runId}/messages${query}`);
      if (generation !== pollGeneration) {
        return;
      }
      applyPayload(payload);
    } catch (error) {
      if (generation !== pollGeneration) {
        return;
      }
      // A run that has vanished (deleted / no longer visible) must not be polled
      // forever — stop on a terminal 404. Other failures are transient: keep the
      // last known state and retry while active.
      if (error instanceof ApiError && error.status === 404) {
        stopPolling();
        return;
      }
    }

    // Keep polling while the run is active OR awaiting approval (paused).
    if (ACTIVE_STATUSES.has(runStatus) && generation === pollGeneration) {
      pollTimer = setTimeout(() => pollOnce(generation), POLL_INTERVAL_MS);
    }
  }

  const TERMINAL_STATUSES = new Set(["completed", "failed"]);

  function isNearBottom() {
    if (!timelineEl) {
      return true;
    }
    const distance = timelineEl.scrollHeight - timelineEl.scrollTop - timelineEl.clientHeight;
    return distance <= NEAR_BOTTOM_PX;
  }

  function applyPayload(payload) {
    // A local Stop (or a completion we already saw) is authoritative: never let a
    // stale in-flight poll resurrect a terminal run back to paused/running.
    if (TERMINAL_STATUSES.has(runStatus) && !TERMINAL_STATUSES.has(payload.status)) {
      return;
    }
    // Snapshot whether the user is pinned to the bottom BEFORE the DOM grows, so a
    // scroll-up to read history is never yanked back down by fresh model output.
    const stickToBottom = isNearBottom();
    const prevStatus = runStatus;
    runStatus = payload.status;
    // When a run ends as FAILED/stopped, re-fill the composer with the kickoff so
    // "Run agent again" is immediately Send-able — but never for a `completed` run
    // (the generation is done, so re-prefilling would leave stale text in a
    // composer that disables a beat later), and never clobber steering text the
    // user is mid-typing.
    if (
      !TERMINAL_STATUSES.has(prevStatus) &&
      payload.status === "failed" &&
      !completedGeneration &&
      steeringText.trim() === ""
    ) {
      steeringText = defaultKickoff(generationId);
    }
    // UPSERT-BY-ID: the poll re-sends messages whose content/reasoning/tool_calls
    // filled in AFTER the row was first created (a streaming assistant shell).
    // Replace any message we already have by id, and append the genuinely new
    // ones — so a late-enriched turn stops being an empty bubble instead of being
    // appended a second time.
    //
    // Sort by (conversation_order ?? id): a delivered steering message carries a
    // delivery-time conversation_order that positions it after the tool batch it
    // was enqueued amid; ordinary + still-pending steering messages have a null
    // conversation_order and fall back to id (so a pending bubble renders near
    // where it was enqueued, then re-sorts to the end once delivered).
    if (Array.isArray(payload.messages) && payload.messages.length > 0) {
      const byId = new Map(messages.map((m) => [m.id, m]));
      for (const incoming of payload.messages) {
        byId.set(incoming.id, incoming);
      }
      const orderOf = (m) => (m.conversation_order ?? m.id);
      messages = Array.from(byId.values()).sort((a, b) => orderOf(a) - orderOf(b));
    }
    if (payload.after_id) {
      afterId = payload.after_id;
    }
    if (payload.updated_since) {
      updatedSince = payload.updated_since;
    }
    // Budget/steps progress readout — refreshed every poll straight from the
    // server (loop.cost / loop.step_count + the bound model's effective caps).
    // cost/budget_limit are serialized as decimal strings (e.g. "0.42") — coerce
    // to Number so the template's toFixed(2) works.
    if (payload.cost != null) {
      runCost = Number(payload.cost);
    }
    if (payload.step_count != null) {
      runStepCount = payload.step_count;
    }
    if (payload.budget_limit != null) {
      runBudgetLimit = Number(payload.budget_limit);
    }
    if (payload.steps_limit != null) {
      runStepsLimit = payload.steps_limit;
    }
    if (payload.tokens_total != null) {
      runTokensTotal = Number(payload.tokens_total);
    }
    if (payload.tokens_limit != null) {
      runTokensLimit = Number(payload.tokens_limit);
    }
    runModelName = payload.llm_model_name ?? runModelName;
    // Authoritative every poll (including back to null, e.g. an un-confirm that
    // clears it) — this is the run's own record of why it ended, never invented
    // here.
    runErrorMessage = payload.error_message ?? null;

    const nextPending = payload.pending_approval || null;
    const gateAppeared = !!nextPending && nextPending.id !== pendingApproval?.id;
    // A fresh (or different) pending approval clears the stale drift banner.
    if (!nextPending || gateAppeared) {
      driftBanner = false;
    }
    pendingApproval = nextPending;
    reopened = payload.reopened === true;

    // Live table refresh: detect a NEWLY-completed row-mutating tool call. We
    // scan every loaded message's tool_calls (the poll upserts a call's row when
    // its result fills in AFTER it was first created — is_success flips from
    // null to true then), and fire ONCE per poll if any completed mutating call
    // id we haven't seen before appears. Tracking ids (not a bare count) means a
    // re-sent/enriched tool_call never re-fires, and non-mutating tools are
    // filtered out so they can't cause a false refresh.
    let sawNewMutation = false;
    for (const message of messages) {
      if (!Array.isArray(message.tool_calls)) {
        continue;
      }
      for (const toolCall of message.tool_calls) {
        if (
          toolCall.is_success === true &&
          ROW_MUTATING_TOOLS.has(toolCall.function_name) &&
          !seenMutatingToolCallIds.has(toolCall.id)
        ) {
          seenMutatingToolCallIds.add(toolCall.id);
          sawNewMutation = true;
        }
      }
    }
    if (sawNewMutation) {
      onRowsChanged();
    }

    // Follow the freshest model output only when the user was already at the
    // bottom; wait for Svelte to render the new/enriched messages first. But a
    // NEWLY-arrived HITL gate must never be missed — force it into view even if
    // the user had scrolled up to read history.
    if (stickToBottom || gateAppeared) {
      tick().then(scrollToBottom);
    }
  }

  function scrollToBottom() {
    if (!timelineEl) {
      return;
    }
    timelineEl.scrollTo({ top: timelineEl.scrollHeight, behavior: "smooth" });
  }

  async function stopRun() {
    if (!runId || stopping) {
      return;
    }
    stopping = true;
    // Stop polling synchronously so no scheduled/in-flight poll can resurrect the
    // gate after we clear it below.
    stopPolling();
    try {
      const response = await apiRequest(`/api/agent_runs/${runId}/stop`, { method: "POST" });
      runStatus = response.status;
      pendingApproval = null; // Stop clears the gate.
      // One final read so the panel can SAY why it ended. The stop endpoint
      // answers with {id, status} only, and we deliberately stopped polling —
      // without this the run's own reason ("Stopped by user", written by
      // agent_runs#stop) never reaches the client and the operator gets a bare
      // `failed` badge. Safe to poll now: the loop is terminal server-side, so
      // this fetch does not reschedule, applyPayload's terminal guard blocks any
      // resurrection, and `stop` has already superseded the pending approval, so
      // the gate cannot come back. The reason still comes from the server — we
      // never write that sentence ourselves.
      beginPolling();
    } catch (_error) {
      // ignore; the next manual poll (if any) reconciles
    } finally {
      stopping = false;
    }
  }

  async function decide(decision, reason) {
    if (!runId || !pendingApproval || deciding) {
      return;
    }
    deciding = true;
    try {
      await apiRequest(`/api/agent_runs/${runId}/approvals/${pendingApproval.id}`, {
        method: "POST",
        body: { decision, reason: reason || undefined }
      });
      // Decision accepted; the run resumes. Refetch immediately to pick up the
      // advance, then keep polling.
      driftBanner = false;
      pendingApproval = null;
      beginPolling();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409 && error.payload?.error === "data_changed") {
        // Drift: replace the card with the refreshed approval + a re-review banner.
        pendingApproval = error.payload.approval || pendingApproval;
        driftBanner = true;
      } else if (error instanceof ApiError && error.status === 409) {
        // Decided / stopped / superseded elsewhere: refetch and re-render.
        driftBanner = false;
        beginPolling();
      } else {
        startError = error?.message || "Could not record the decision.";
      }
    } finally {
      deciding = false;
    }
  }

  // A gate is auto-approvable unless it's a terminal (complete) gate whose fresh
  // verify failed — same rule that disables the manual Approve button.
  function autoApprovable(a) {
    if (!a) {
      return false;
    }
    const verify = a.evidence?.verify || null;
    const verifyAccepted = verify ? verify.accepted === true : true;
    return !(a.kind === "terminal" && !verifyAccepted);
  }

  // Fire an auto-approval for the current gate when armed. Runs reactively on every
  // poll (pendingApproval is reassigned each cycle) and again if verify later flips
  // to passing; the per-gate id set fires it at most once per gate, and never while
  // a decision is already in flight. Drift (409) keeps the same gate id, so a
  // data-changed gate is left for the human rather than blindly re-approved.
  function maybeAutoApprove() {
    if (!pendingApproval || runStatus !== "paused" || deciding) {
      return;
    }
    if (!autoApprove && autoApproveRemaining <= 0) {
      return;
    }
    // Next-N NEVER auto-approves the TERMINAL completion gate — the human always
    // consciously confirms completing the generation. Full YOLO still can.
    if (!autoApprove && pendingApproval.kind === "terminal") {
      return;
    }
    if (autoApprovedGateIds.has(pendingApproval.id) || !autoApprovable(pendingApproval)) {
      return;
    }
    autoApprovedGateIds.add(pendingApproval.id);
    if (!autoApprove) {
      autoApproveRemaining -= 1;
    }
    decide("approve");
  }

  $: pendingApproval, runStatus, autoApprove, autoApproveRemaining, deciding, maybeAutoApprove();

  // Status-bar background by mode: YOLO = orange (near red), armed-N = yellow,
  // no auto-approve = white (default).
  $: autoApproveBarClass = autoApprove
    ? "bg-[#ffe3d3] border-[#ea580c] text-[#9a3412]"
    : autoApproveRemaining > 0
      ? "bg-[#fef9c3] border-[#ca8a04] text-[#854d0e]"
      : "bg-white border-neutral-200 text-neutral-600";

  // Post the composer text to the steering endpoint. We DON'T optimistically
  // insert: the existing poll renders the new pending bubble within a cycle, and
  // (crucially) we then trigger an immediate poll so it appears without waiting.
  // A 409 means the run went terminal between render and send — surface it.
  async function sendSteering() {
    const content = steeringText.trim();
    if (!content || !runId || sendingSteering || !isActive) {
      return;
    }
    sendingSteering = true;
    steeringError = null;
    try {
      await apiRequest(`/api/agent_runs/${runId}/steering`, {
        method: "POST",
        body: { content }
      });
      steeringText = "";
      // Pull the new pending bubble in immediately rather than waiting a cycle.
      beginPolling();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        steeringError = "This run has finished — the message could not be sent.";
      } else if (error instanceof ApiError && error.status === 422) {
        steeringError = "Message can't be blank.";
      } else {
        steeringError = error?.message || "Could not send the message.";
      }
    } finally {
      sendingSteering = false;
    }
  }

  // Continue a REOPENED (un-confirmed) run: deliver the composer follow-up and
  // resume the SAME loop (paused → queued + a fresh LLM turn) — it keeps its full
  // reasoning history. Content is optional (the un-confirm fact is already queued).
  let continuing = false;
  async function continueRun() {
    if (!runId || continuing) {
      return;
    }
    const content = steeringText.trim();
    continuing = true;
    startError = null;
    try {
      const response = await apiRequest(`/api/agent_runs/${runId}/continue`, {
        method: "POST",
        body: content ? { content } : {}
      });
      steeringText = "";
      runStatus = response.status;
      beginPolling();
    } catch (error) {
      startError = error?.message || "Could not continue the run.";
    } finally {
      continuing = false;
    }
  }

  // Composer submit is context-aware: a REOPENED run CONTINUES the same loop; a
  // LIVE run STEERS; an idle generation STARTS a run (Start === Send).
  function onComposerSubmit() {
    if (reopenedPaused) {
      continueRun();
    } else if (isActive) {
      sendSteering();
    } else if (canStart) {
      startRun();
    }
  }

  function onComposerKeydown(event) {
    // Enter sends; Shift+Enter inserts a newline (ChatGPT semantics).
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onComposerSubmit();
    }
  }

  function markSteeringBusy(id, busy) {
    const next = new Set(steeringBusyIds);
    if (busy) {
      next.add(id);
    } else {
      next.delete(id);
    }
    steeringBusyIds = next;
  }

  // Reconcile a raced delivery: the pending message was materialized by the gem
  // between the UI rendering its controls and the edit/cancel landing (gem raised
  // -> 409). A fresh poll flips it to a delivered user bubble and the affordances
  // vanish, so we just refresh and clear the busy flag.
  // A cancel/edit lost the race: the message is no longer a pending steering
  // row (delivered → 409, or already removed elsewhere → 404). Re-poll to render
  // the authoritative state; callers prune a now-deleted ghost first.
  function reconcileRacedSteering() {
    steeringError = "That message is no longer pending.";
    beginPolling();
  }

  async function cancelSteering(messageId) {
    if (!runId || steeringBusyIds.has(messageId)) {
      return;
    }
    markSteeringBusy(messageId, true);
    steeringError = null;
    try {
      await apiRequest(`/api/agent_runs/${runId}/steering/${messageId}`, { method: "DELETE" });
      // Drop it locally — the upsert-by-id poll only adds/updates rows, it never
      // removes one that vanished server-side, so we must prune the bubble here.
      messages = messages.filter((m) => m.id !== messageId);
      beginPolling();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // The gem already delivered it — the row still exists (un-hidden), so let
        // the poll flip it to a delivered bubble in place; don't prune.
        reconcileRacedSteering();
      } else if (error instanceof ApiError && error.status === 404) {
        // Already cancelled/removed (e.g. in another tab). The upsert poll can't
        // drop a vanished row, so prune the ghost locally, then reconcile.
        messages = messages.filter((m) => m.id !== messageId);
        reconcileRacedSteering();
      } else {
        steeringError = error?.message || "Could not cancel the message.";
      }
    } finally {
      markSteeringBusy(messageId, false);
    }
  }

  async function editSteering(messageId, content) {
    if (!runId || steeringBusyIds.has(messageId)) {
      return;
    }
    markSteeringBusy(messageId, true);
    steeringError = null;
    try {
      const updated = await apiRequest(`/api/agent_runs/${runId}/steering/${messageId}`, {
        method: "PATCH",
        body: { content }
      });
      // Upsert the edited row locally; the poll reconciles regardless.
      messages = messages.map((m) => (m.id === messageId ? { ...m, ...updated } : m));
      beginPolling();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Delivered mid-edit — let the poll render it as a sent message in place.
        reconcileRacedSteering();
      } else if (error instanceof ApiError && error.status === 404) {
        // Cancelled/removed elsewhere — prune the ghost the poll can't remove.
        messages = messages.filter((m) => m.id !== messageId);
        reconcileRacedSteering();
      } else {
        steeringError = error?.message || "Could not edit the message.";
      }
    } finally {
      markSteeringBusy(messageId, false);
    }
  }

  // Y toggles YOLO (auto-approve every gate) — the statusline shortcut, mirroring
  // the approval card's A/R. Guarded the same way: ignored with a modifier held,
  // while typing in a field, and while the auto-approve bar isn't shown (a
  // completed generation with no run). A/R live on the ApprovalCard's own window
  // listener, so there's no key overlap here.
  function isTypingTarget(el) {
    if (!el) return false;
    const tag = el.tagName;
    return el.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
  }

  function onWindowKeydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTypingTarget(event.target)) return;
    if (completedGeneration && !runStatus) return;
    if (event.key.toLowerCase() === "y") {
      event.preventDefault();
      autoApprove = !autoApprove;
    }
  }

  // ----- WHY THE RUN ENDED (M7) -----
  // The copy lives in <RunStopReason> / utils/stopReason.js, shared with the
  // archive. Here we only decide WHEN it belongs on screen: exactly when the run
  // is over.
  //
  // A single TERMINAL check, deliberately — not a list of pause-shaped
  // exceptions. `find_visible_loop!` runs AgentRuns::LimitStopReconciler before
  // it serializes, so a limit stop is already `failed` in the first payload the
  // client ever sees; there is no "paused but really over" state to catch. What
  // a `paused` fallback DID catch was the one pause the reconciler refuses — an
  // ADMIN FREEZE — where the run is parked, still active, still Stoppable, and
  // telling that operator "the run is over, start a fresh one" is simply false.
  $: showStopReason = !!runErrorMessage && TERMINAL_STATUSES.has(runStatus);

  // A counter that has reached its cap is the one that ended the run — say so in
  // colour, so the stop explains itself before anyone reads the error text.
  function counterClass(value, limit) {
    const reached =
      value != null && limit != null && Number(limit) > 0 && Number(value) >= Number(limit);
    return reached ? "font-bold text-danger-text" : "font-semibold text-neutral-800";
  }

  function statusBadgeClass(status) {
    if (status === "completed") {
      return "bg-success-bg text-success-text border-success-border/20";
    }
    if (status === "failed") {
      return "bg-danger-bg text-danger-text border-danger-border/20";
    }
    if (status === "paused") {
      return "bg-warning-bg text-warning-text border-warning-border/30";
    }
    if (status === "running" || status === "queued" || status === "init") {
      return "bg-brand-50 text-brand-600 border-brand-200/50 animate-pulse";
    }
    return "bg-neutral-100 text-neutral-600 border-neutral-200";
  }

</script>

<svelte:window on:keydown={onWindowKeydown} />

<section class="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-3" data-testid="agent-run-panel">
  <div class="flex items-center justify-between gap-3 flex-wrap">
    <h4 class="text-xs font-bold text-neutral-700 tracking-wider">Agent run</h4>
    <div class="flex items-center gap-2 flex-wrap">
      {#if runStatus}
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full text-[0.62rem] font-bold uppercase tracking-wider border {statusBadgeClass(runStatus)}"
          data-testid="run-status"
        >
          {runStatus}
        </span>
      {/if}
      {#if runModelName}
        <!-- Which model this run is bound to: the run's identity, so it sits with
             the status badge rather than competing for width with the three
             limit counters in the status bar. -->
        <span
          data-testid="run-model-name"
          class="text-[0.68rem] text-neutral-500 max-w-[12rem] truncate"
          title={`Model: ${runModelName}`}
        >{runModelName}</span>
      {/if}
      <button
        type="button"
        class="inline-flex items-center justify-center h-6 w-6 rounded border border-neutral-200 bg-white text-sm hover:bg-neutral-100"
        data-testid="reasoning-toggle"
        aria-pressed={showReasoning}
        title={showReasoning ? "Hide model reasoning" : "Show model reasoning"}
        on:click={toggleShowReasoning}
      >
        <span aria-hidden="true">{showReasoning ? "💡" : "🔅"}</span>
        <span class="sr-only">{showReasoning ? "Hide model reasoning" : "Show model reasoning"}</span>
      </button>
      {#if runId}
        <a
          href={`/api/agent_runs/${runId}/transcript`}
          target="_blank"
          rel="noopener"
          class="btn-secondary py-1 px-2.5 text-xs"
          data-testid="export-transcript"
        >
          Export transcript
        </a>
      {/if}
      {#if isActive}
        <button
          type="button"
          class="btn-secondary py-1 px-2.5 text-xs bg-danger-bg text-danger-text border border-danger-border/20"
          data-testid="run-stop"
          disabled={stopping}
          on:click={stopRun}
        >
          {stopping ? "Stopping…" : "Stop"}
        </button>
      {:else}
        <button
          type="button"
          class="btn-primary py-1 px-2.5 text-xs"
          data-testid="run-start"
          disabled={starting || completedGeneration}
          on:click={startRun}
        >
          {starting ? "Starting…" : runStatus ? "Run agent again" : "Run agent"}
        </button>
      {/if}
    </div>
  </div>

  {#if completedGeneration && !runStatus}
    <p class="text-xs text-neutral-500" data-testid="run-completed-note">
      This generation is completed — an agent run can't do useful work here.
    </p>
  {/if}

  {#if startError}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1.5 text-xs" role="alert" data-testid="run-error">
      {startError}
    </p>
  {/if}

  <!-- WHY IT ENDED — in the LIVE panel, not only the archive. An operator
       watching a run that a safety limit stops must not have to click into the
       history rail to find out what happened; the archive renders the SAME
       component, so a reload never downgrades the explanation. -->
  {#if showStopReason}
    <RunStopReason errorMessage={runErrorMessage} />
  {/if}

  {#if runStatus}
    <div
      class="space-y-2 max-h-[32rem] overflow-y-auto scroll-smooth"
      data-testid="run-timeline"
      bind:this={timelineEl}
    >
      <RunTimeline
        {messages}
        {showReasoning}
        {awaitingApproval}
        {pendingApproval}
        {deciding}
        {driftBanner}
        {steeringBusyIds}
        onApprove={() => decide("approve")}
        onReject={(reason) => decide("reject", reason)}
        onCancelSteering={cancelSteering}
        onEditSteering={editSteering}
      />
    </div>
  {/if}

  <!-- ChatGPT-style composer, pinned at the bottom of the panel. It is the ONE
       submit path: while a run is live it STEERS; before a run starts it is
       pre-filled with the kickoff and its Send STARTS the run (Start === Send). It
       is only disabled for a completed generation. The read-only archive view
       never renders this panel, so no composer appears there. -->
  <div class="border-t border-neutral-200 pt-3 space-y-1.5" data-testid="steering-composer">
    {#if steeringError}
      <p class="text-[0.68rem] text-danger-text" role="alert" data-testid="steering-error">
        {steeringError}
      </p>
    {/if}
    {#if canStart && llmModels.length > 1}
      <!-- Model picker: only meaningful before a run starts (Start === Send), and
           only shown when there's an actual choice — a single/empty model list
           still sends whatever id is selected (possibly none), so hiding it here
           loses nothing. -->
      <div class="flex items-center gap-2 text-[0.7rem] text-neutral-600">
        <label for="run-model-select" class="font-semibold">Model</label>
        <select
          id="run-model-select"
          class="form-input py-1 px-2 text-xs max-w-xs"
          data-testid="run-model-select"
          bind:value={selectedLlmModelId}
        >
          {#each llmModels as llmModel, index (llmModel.id)}
            <!-- index 0 is the lowest `position` (server returns position ASC) —
                 the pre-selected default; mark it so the choice is obvious. -->
            <option value={llmModel.id}>{llmModel.friendly_name}{index === 0 ? " · default" : ""}</option>
          {/each}
        </select>
      </div>
    {/if}
    <div class="flex items-end gap-2">
      <textarea
        class="flex-1 text-xs text-neutral-800 border border-neutral-200 rounded-md p-2 resize-y disabled:bg-neutral-100 disabled:text-neutral-400"
        rows="2"
        placeholder={reopenedPaused
          ? "Tell the agent what to change — it kept its full context. Press Continue."
          : isActive
            ? "Message the agent… (Enter to send, Shift+Enter for a new line)"
            : canStart
              ? "This message starts the run — edit it or press Send (Enter to send)"
              : "Start a run to message the agent"}
        bind:value={steeringText}
        on:keydown={onComposerKeydown}
        disabled={(!isActive && !canStart) || sendingSteering || starting || continuing}
        data-testid="steering-input"
      ></textarea>
      <button
        type="button"
        class="btn-primary py-1.5 px-3 text-xs"
        data-testid="steering-send"
        disabled={reopenedPaused
          ? continuing
          : (!isActive && !canStart) || sendingSteering || starting || !steeringText.trim()}
        on:click={onComposerSubmit}
      >
        {#if reopenedPaused}
          {continuing ? "Continuing…" : "Continue ▶"}
        {:else if isActive}
          {sendingSteering ? "Sending…" : "Send"}
        {:else}
          {starting ? "Starting…" : "Send ▶"}
        {/if}
      </button>
    </div>
  </div>

  <!-- Auto-approve STATUS BAR, pinned UNDER the composer (the statusline pattern —
       like Claude Code, not the header). Background reflects the mode: YOLO
       (approve all) = orange, armed next-N = yellow, off (manual) = white. -->
  {#if !(completedGeneration && !runStatus)}
    <div
      class="flex flex-col gap-1 text-xs mt-2 px-2.5 py-1.5 rounded-md border {autoApproveBarClass}"
      data-testid="auto-approve-controls"
    >
      <!-- Row 1: the approval mode. Row 2 (below) is the run's progress. Two
           EXPLICIT rows rather than one wrapping line: with three counters the
           single row overflowed at the default viewport and dropped the token
           counter onto a line of its own, which is exactly the "the limit that
           fired is somewhere else" problem M7 exists to end. -->
      <div class="flex items-center gap-2.5 flex-wrap">
      <span class="inline-flex items-center gap-1.5 select-none">
        <button
          type="button"
          role="switch"
          aria-checked={autoApprove}
          data-testid="yolo-toggle"
          title="YOLO — auto-approve every gate"
          on:click={() => (autoApprove = !autoApprove)}
          class="relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors {autoApprove ? 'bg-[#ea580c]' : 'bg-neutral-300'}"
        >
          <span class="inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform {autoApprove ? 'translate-x-3.5' : 'translate-x-0.5'}"></span>
        </button>
        <span class="font-bold tracking-wide">YOLO <span class="font-normal opacity-70">(Y)</span></span>
      </span>

      <span class="text-neutral-300">·</span>

      <span class="inline-flex items-center gap-1.5">
        <span>Auto-approve next</span>
        <input
          type="number"
          min="0"
          bind:value={autoApproveRemaining}
          class="w-14 rounded border border-neutral-300 bg-white px-1.5 py-0.5 text-neutral-800"
          data-testid="auto-approve-n"
        />
        <span>steps</span>
      </span>

      <span class="ml-auto font-semibold" data-testid="auto-approve-status">
        {#if autoApprove}
          ● YOLO — approving all
        {:else if autoApproveRemaining > 0}
          approving next {autoApproveRemaining}
        {:else}
          manual — you approve each
        {/if}
      </span>
      </div>

      {#if runStatus}
        <!-- Row 2: the three counters that can END this run, on ONE line and in
             one visual register — visible to ALL roles (the limits and
             llm_model_name come off the /messages poll payload, clamped
             server-side to the system caps). A counter that has reached its cap
             turns red, so the stop names itself before anyone reads text. -->
        <div
          class="flex items-center gap-2.5 border-t border-black/10 pt-1 tabular-nums min-w-0"
          data-testid="run-progress"
        >
          <!-- ONLY the three counters live here. The model name used to sit
               alongside them and, on a narrow column with a long friendly_name,
               pushed the token counter onto a line of its own — the exact "the
               limit that fired is somewhere else" failure M7 exists to end. It
               now lives in the header, beside the run's status. -->
          <div class="flex items-center gap-2.5 flex-wrap min-w-0" data-testid="run-counters">
          <span class="text-neutral-600 whitespace-nowrap" data-testid="run-budget">
            <span class={counterClass(runCost, runBudgetLimit)}>${(runCost ?? 0).toFixed(2)}</span> / ${(runBudgetLimit ?? 0).toFixed(2)}
          </span>
          <span class="text-neutral-300">·</span>
          <span class="text-neutral-600 whitespace-nowrap" data-testid="run-steps-budget">
            <span class={counterClass(runStepCount, runStepsLimit)}>{runStepCount ?? 0}</span> / {runStepsLimit ?? 0} steps
          </span>
          <span class="text-neutral-300">·</span>
          <span
            class="text-neutral-600 whitespace-nowrap"
            data-testid="run-tokens"
            title="Prompt + completion tokens, including cached tokens (worst case)"
          >
            <span class={counterClass(runTokensTotal, runTokensLimit)}>{groupDigits(runTokensTotal ?? 0)}</span> / {groupDigits(runTokensLimit ?? 0)} tokens
          </span>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</section>
